<template>
    <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" modal
        :header="header" :style="{ width: '34rem' }" :breakpoints="{ '640px': '95vw' }">
        <div class="flex flex-col gap-3 text-sm">
            <p><strong>Nothing was saved.</strong></p>
            <p v-if="conflictLabel">
                On this login<span v-if="details.affiliateSystem"> ({{ details.affiliateSystem }})</span>,
                campaign key <code>{{ details.internalNote }}</code> already belongs to
                <strong>{{ conflictLabel }}</strong>.
            </p>
            <p>
                Every link on the same login needs its own campaign key, otherwise postbacks
                can't tell the links apart. Please use a different campaign key.
            </p>
            <div class="rounded-md border border-gray-600 p-3">
                <div class="font-medium mb-1">{{ guidance.selfServe ? 'Create a new one yourself' : 'Ask the operator' }}</div>
                <p class="m-0">{{ guidance.text }}</p>
            </div>
        </div>
        <template #footer>
            <Button v-if="guidance.selfServe && details?.loginUrl" :label="loginLabel"
                icon="pi pi-external-link" text @click="openLogin" />
            <Button :label="helpLabel" icon="pi pi-question-circle" text @click="openHelp" />
            <Button label="OK" @click="$emit('update:visible', false)" />
        </template>
    </Dialog>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { newCampaignKeyGuidance, helpQueryForSystem, helpButtonTooltip } from '@/utils/affiliate-system-help';

const props = defineProps({
    visible: { type: Boolean, default: false },
    // 409 body from PUT /siteLinks/:id with code INTERNAL_NOTE_TAKEN. Non-admins get
    // only { message, code, internalNote }, so every field below is optional.
    details: { type: Object, default: null },
});
defineEmits(['update:visible']);

const router = useRouter();

const header = computed(() => props.details?.internalNote
    ? `Campaign key ${props.details.internalNote} is already in use`
    : 'Campaign key already in use');

const conflictLabel = computed(() => {
    const c = props.details?.conflict;
    if (!c?.advertiser) return '';
    const name = [c.advertiser, c.market].filter(Boolean).join(' ');
    return c.publisher ? `${name} — ${c.publisher}` : name;
});

const guidance = computed(() => newCampaignKeyGuidance(props.details?.affiliateSystem));
const helpLabel = computed(() => helpButtonTooltip(props.details?.affiliateSystem));
const loginLabel = computed(() => `Open ${props.details?.affiliateSystem || 'affiliate'} login`);

function openHelp() {
    const url = router.resolve({ name: 'Help', query: helpQueryForSystem(props.details?.affiliateSystem) }).href;
    window.open(url, '_blank', 'noopener,noreferrer');
}

function openLogin() {
    window.open(props.details.loginUrl, '_blank', 'noopener,noreferrer');
}
</script>
